import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { errMsg } from "./ResolveDialog";
import { EmailPreviewCard } from "./EmailPreviewCard";
import { EmailWordingCard } from "./EmailWordingCard";
import { BrandColorField, LogoField } from "./BrandingFields";

const describe = (job, r) => {
  if (job === "invite") return `${r.invited} invitation(s) sent, ${r.reminded} reminder(s), ${r.skipped_opt_out} skipped (opted out)`;
  if (job === "expire") return `${r} link(s) expired`;
  if (job === "escalation") return `${r} alert(s) escalated to Owners`;
  return `${r} digest email(s) sent`;
};
const JOBS = [["invite", "Send due invitations & reminders"], ["expire", "Expire overdue links"], ["escalation", "Escalate unacknowledged alerts"], ["digest", "Send staff digest"]];

export function SettingsPanel({ isLead }) {
  const [s, setS] = useState(null);
  const [digest, setDigest] = useState(true);
  useEffect(() => {
    api.get("/checkin-settings").then((r) => setS(r.data));
    api.get("/checkin-staff-prefs/me").then((r) => setDigest(r.data.digest_enabled));
  }, []);
  async function save() {
    try {
      const r = await api.put("/checkin-settings", { clinic_name: s.clinic_name, contact_phone: s.contact_phone, contact_email: s.contact_email, brand_color: s.brand_color });
      setS((x) => ({ ...x, ...r.data }));
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed", { description: errMsg(e) }); }
  }
  async function toggleDigest(v) {
    setDigest(v);
    await api.put("/checkin-staff-prefs/me", { digest_enabled: v }).catch(() => setDigest(!v));
  }
  async function run(job) {
    try {
      const r = await api.post("/checkin-jobs/run", { job });
      toast.success("Job finished", { description: describe(job, r.data.result) });
    } catch (e) { toast.error("Job failed", { description: errMsg(e) }); }
  }
  if (!s) return null;
  const set = (k) => (e) => setS((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="checkin-settings">
      <Card className="space-y-3 p-4">
        <h3 className="font-display font-semibold">Clinic details & branding on client pages & emails</h3>
        <div><Label>Clinic name</Label><Input value={s.clinic_name} onChange={set("clinic_name")} disabled={!isLead} data-testid="settings-clinic-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Contact phone</Label><Input value={s.contact_phone} onChange={set("contact_phone")} disabled={!isLead} data-testid="settings-contact-phone" /></div>
          <div><Label>Contact email</Label><Input value={s.contact_email} onChange={set("contact_email")} disabled={!isLead} data-testid="settings-contact-email" /></div>
        </div>
        <BrandColorField value={s.brand_color || "#18181b"} onChange={(v) => setS((x) => ({ ...x, brand_color: v }))} disabled={!isLead} />
        {isLead && <Button onClick={save} data-testid="settings-save">Save</Button>}
        <LogoField logoUrl={s.logo_url} onChange={(d) => setS((x) => ({ ...x, logo_url: d.logo_url }))} isLead={isLead} />
        <p className="text-xs text-zinc-500">Invitations go at 9:00 am Melbourne time · staff digest 8:00 am weekdays · urgent alerts escalate to Owners after 2 hours, high after 1 business day.</p>
      </Card>
      <div className="space-y-4">
        <Card className="flex items-center justify-between gap-4 p-4">
          <div><h3 className="font-display font-semibold">My daily digest</h3><p className="text-xs text-zinc-500">8:00 am weekday email of my unresolved items. Safety escalations are always sent.</p></div>
          <Switch checked={digest} onCheckedChange={toggleDigest} data-testid="settings-digest-toggle" />
        </Card>
        {isLead && (
          <Card className="space-y-2 p-4">
            <h3 className="font-display font-semibold">Run scheduled jobs now</h3>
            <div className="flex flex-wrap gap-2">{JOBS.map(([k, l]) => <Button key={k} size="sm" variant="outline" onClick={() => run(k)} data-testid={`settings-run-${k}`}>{l}</Button>)}</div>
          </Card>
        )}
      </div>
      <div className="lg:col-span-2"><EmailWordingCard isLead={isLead} /></div>
      <div className="lg:col-span-2"><EmailPreviewCard /></div>
    </div>
  );
}
